import {
  Box,
  Container,
  InputLabel,
  ListItemText,
  Modal,
  Select,
  SelectChangeEvent,
  Toolbar,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import CustomTable from "../../../components/Table/CustomTable";
import { memo, SyntheticEvent, useCallback, useEffect, useState } from "react";
import {
  GarminData,
  ROLES,
  Student,
  User,
  UserSchema,
} from "../../../types/user";
import { Column } from "../../../types/table";
import { TableBar } from "../../../components/styled/AppBar";
import { useAppSelector, useAppThunkDispatch } from "../../../store/hooks";
import { removeUser } from "../../../store/actions/userActions";
import Loading from "../../../components/Loading";
import {
  deleteUser,
  deleteUserPerm,
  fetchStudents,
} from "../../../services/api/users.api";
import { getRole } from "../../../services/api/utils.api";
import { PermissionControl } from "../../../components/Permission";
import { permissions } from "../../../utils/permissions";
import { getTitle } from "../../../utils/getSchoolName";
import { selectSchools } from "../../../store/selectors/schoolSelectors";
import { usePathname } from "../../../hooks/usePathname";
import { useNavigate } from "react-router-dom";
import {
  menuStyles,
  StyledFormControl,
} from "../../../components/styled/StyledFormSelect";
import { getSchools } from "../../../store/effects/schoolEffects";
import GenerateStudentsForm from "../../../components/GenerateStudentsForm";
import { handleApiError } from "../../../utils/handleApiError";
import AlertDialog from "../../../components/Alert";
import { debounce } from "lodash";
import StudentSearch from "../../../components/Toolbars/StudentSearch";
import ActionButtons from "../../../components/Toolbars/ActionButtons";

const PLAYER_TEXT = "Player";

const Players = () => {
  const role = getRole();
  const [selectedStudentOrGarmin, setSelectedStudentOrGarmin] = useState(""); //username or garmin id
  const [selectedCase, setSelectedCase] = useState<boolean | undefined>(
    undefined
  );
  const [selectedSchool, setSelectedSchool] = useState("");
  const schools = useAppSelector(selectSchools);
  const [data, setData] = useState<Student[] | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppThunkDispatch();
  const pathname = usePathname();
  const router = useNavigate();
  const [error, setError] = useState<string | undefined>();
  const [alert, setAlert] = useState<string | undefined>();
  const [rowDel, setRowDel] = useState<User | undefined>();
  const [rowRem, setRowRem] = useState<User | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [rpp, setRpp] = useState(10);
  const [totalRows, setTotalRows] = useState<number | undefined>();

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };

  const [showLoader, setShowLoader] = useState(false);

  // Delay the loader appearance
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLoading) setShowLoader(true);
    }, 3000); // Show loader only if loading takes more than 3s

    return () => clearTimeout(timer); // Cleanup timeout if component unmounts or loading completes early
  }, [isLoading]);

  // console.log(students);

  const fetchData = (
    page = 1,
    rpp = 10,
    data = "",
    school = "",
    caseStudent?: boolean
  ) => {
    setIsLoading(true);
    // console.log("here", data, school, caseStudent);
    fetchStudents(rpp, page, data, school, caseStudent)
      .then((res) => {
        console.log("STUDENTS:", res.data);
        setData(res.data.data.students || []);
        setTotalRows(res.data.data.total_count);
      })
      .catch((e) => {
        handleApiError(e, setError);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    dispatch(getSchools());
    fetchData(currentPage, rpp);
  }, [dispatch]);

  const handlePaginationOptions = (page: number, rowsPerPage: number) => {
    setCurrentPage(page);
    setRpp(rowsPerPage);
    fetchData(
      page,
      rowsPerPage,
      selectedStudentOrGarmin,
      selectedSchool,
      selectedCase
    );
  };

  const handleRemoveDelete = () => {
    if (rowRem) {
      const row = rowRem;
      console.log("Soft delete student", row);
      deleteUser({ ...row, role: ROLES.STUDENT })
        .then((res) => {
          if (res.status === 200) {
            // dispatch(removeUser(row._id!));
            fetchData(
              currentPage,
              rpp,
              selectedStudentOrGarmin,
              selectedSchool,
              selectedCase
            );
            setRowRem(undefined);
          }
        })
        .catch((e) => {
          handleApiError(e, setError);
        });
    } else if (rowDel) {
      const row = rowDel;
      console.log("Hard delete student", row);
      deleteUserPerm({ ...row, role: ROLES.STUDENT })
        .then((res) => {
          if (res.status === 200) {
            // dispatch(removeUser(row._id!));
            fetchData(
              currentPage,
              rpp,
              selectedStudentOrGarmin,
              selectedSchool,
              selectedCase
            );
            setRowDel(undefined);
          }
        })
        .catch((e) => {
          handleApiError(e, setError);
        });
    }
  };

  const ProcessRows = (data: Student[]): UserSchema<Student>[] => {
    return data.map((row) => {
      const actions = [
        {
          label: "Edit",
          action: (e: React.MouseEvent<HTMLElement>) => {
            router(`${pathname}/${row._id}/`);
          },
          show: role ? permissions.pages.players.update.includes(role) : false,
        },
        {
          label: "Remove",
          action: (e: React.MouseEvent<HTMLElement>) => {
            // console.log(row._id);

            setAlert(
              `User ${row._id} will be removed from dashboard. Their data will remain in storage. Do you wish to proceed?`
            );
            setRowRem(row);
          },
          show: role ? permissions.pages.players.delete.includes(role) : false,
        },
        {
          label: "Delete",
          action: (e: React.MouseEvent<HTMLElement>) => {
            console.log(row);
            setAlert(
              `This user and its data will be deleted permanently. Do you wish to permanently delete ${row._id}?`
            );
            setRowDel(row);
          },
          show: role ? permissions.pages.players.delete.includes(role) : false,
        },
      ];

      !actions.some((action) => action.show === true) &&
        actions.push({
          label: "No Available Actions",
          action: (e: React.MouseEvent<HTMLElement>) => {
            return;
          },
          show: true,
        });

      // const school =
      //   row.schools && row.schools.length > 0
      //     ? getTitle(schools, row.schools[0], row._id) || ""
      //     : row.school
      //     ? getTitle(schools, row.school, row._id) || ""
      //     : "";

      let school = "";

      if (row && row.schools && row.schools.length > 0) {
        school = getTitle(schools, row.schools[0]) || "";
      } else if (row && row.school) {
        school = getTitle(schools, row.school) || "";
      }
      const registered = row.registered ? "Yes" : "No";

      return {
        ...row,
        registered,
        actions,
        schools: [school],
        // garmin_data,
      };
    });
  };

  const columns: Column<Student, keyof Student>[] = [
    // { name: "ID", key: "_id" },
    { name: "USERNAME", key: "_id" },
    // { name: "EMAIL", key: "email" },
    // { name: "UNIQUE ID", key: "offline_code" },
    { name: "REGISTERED", key: "registered" },
    // { name: "TEACHER", key: "teacher" },
    { name: "SCHOOL", key: "schools" },
    // { name: "CASE", key: "tags"},
    { name: "QUESTIONNAIRE COUNT", key: "answered_questionnaires" },
    {
      name: "GARMIN ID",
      key: "garmin_data",
      truncate: {
        getCellContent: (garmin_data: GarminData[] | undefined) => {
          if (!garmin_data || garmin_data.length === 0) {
            return "";
          }
          const lastUsed = garmin_data[garmin_data.length - 1]?._id;
          return lastUsed || "";
        },
        getTooltip: (garmin_data: GarminData[] | undefined) => {
          // return garmin_data ? garmin_data.map((g) => g?._id).join(", ") : "";
          if (!garmin_data || garmin_data.length === 0) {
            return null;
          }

          return (
            <div
              style={{ display: "flex", flexDirection: "column", padding: 2 }}
            >
              <span style={{ fontSize: "12px", fontWeight: "bold" }}>
                Garmin Ids
              </span>
              <ul style={{ listStyle: "none" }}>
                {garmin_data.map((g, index) => (
                  <li key={index}>
                    <span>{g?._id || "N/A"}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        },
      },
    },
    {
      name: "GARMIN LAST USED",
      key: "garmin_data",
      truncate: {
        getCellContent: (garmin_data: GarminData[] | undefined) => {
          if (!garmin_data || garmin_data.length === 0) {
            return "";
          }
          const lastUsed = garmin_data[garmin_data.length - 1]?.last_used;
          return lastUsed ? new Date(lastUsed).toLocaleString("en-GB") : "N/A";
        },
        getTooltip: (garmin_data: GarminData[] | undefined) => {
          if (!garmin_data || garmin_data.length === 0) {
            return null;
          }

          return (
            <div
              style={{ display: "flex", flexDirection: "column", padding: 2 }}
            >
              <span style={{ fontSize: "12px", fontWeight: "bold" }}>
                Garmin Data
              </span>
              <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {garmin_data.map((g, index) => (
                  <li
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span>{g?._id || "N/A"}</span>
                    <span>
                      {g.last_used
                        ? new Date(g.last_used).toLocaleString("en-GB")
                        : "N/A"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        },
      },
    },
  ];

  const handleClickToCreate = () => {
    router(`${pathname}/create${PLAYER_TEXT}`);
  };

  const handleClickToUpload = () => {
    router(`${pathname}/upload`);
  };

  const handleSearchClick = () => {
    if (selectedStudentOrGarmin === "") {
      handleClear();
    } else {
      setCurrentPage(1);
      fetchData(1, rpp, selectedStudentOrGarmin, selectedSchool, selectedCase);
    }
  };

  const debouncedHandleSearch = useCallback(
    debounce((value, school, caseSt) => {
      console.log("VAL", value, school, caseSt);
      // if (value.length >= 3) {
      setCurrentPage(1);
      fetchData(1, rpp, value, school, caseSt);
      // }
    }, 500),
    []
  );

  const handleChange = (event: { target: { value: any } }) => {
    const newValue = event.target.value;
    setSelectedStudentOrGarmin(newValue);
    debouncedHandleSearch(newValue, selectedSchool, selectedCase);
  };

  const handleClear = (e?: SyntheticEvent) => {
    setSelectedStudentOrGarmin("");
    setCurrentPage(1);
    fetchData(1, rpp, "", selectedSchool, selectedCase);
  };

  const handleSchoolSelect = (e: SelectChangeEvent<string>) => {
    const school = e.target.value;
    setSelectedSchool(school);
    setCurrentPage(1);
    fetchData(1, rpp, selectedStudentOrGarmin, school, selectedCase);
  };

  const getCaseValue = (caseType: boolean | undefined): string => {
    if (caseType === true) return "case";
    if (caseType === false) return "not-case";
    return "all";
  };

  const handleCaseSelect = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    let caseType: boolean | undefined;

    if (value === "case") {
      caseType = true;
    } else if (value === "not-case") {
      caseType = false;
    } else {
      caseType = undefined;
    }

    setSelectedCase(caseType);
    setCurrentPage(1);
    fetchData(1, rpp, selectedStudentOrGarmin, selectedSchool, caseType);
  };

  // console.log("ROWS", data);

  const PlayersTableToolbar = memo(() => {
    return (
      <TableBar position='relative'>
        <Toolbar
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginRight: "10px",
            }}
            width={"60%"}
          >
            <h2>{`${PLAYER_TEXT}s`}</h2>
            <Box
              width={"50%"}
              sx={{
                marginLeft: { xs: "10px", sm: "10px", md: "20px" },
                marginRight: { xs: "10px", sm: "10px", md: "20px" },
              }}
            >
              <StudentSearch
                value={selectedStudentOrGarmin}
                handleChange={handleChange}
                handleSearchClick={handleSearchClick}
                handleClear={handleClear}
              />
            </Box>
            <Box width={"50%"} display='flex' flexDirection='row' gap={2}>
              <StyledFormControl variant='outlined' fullWidth size='small'>
                <InputLabel id='select-school'>School</InputLabel>
                <Select
                  value={selectedSchool}
                  label='School'
                  onChange={(e) => handleSchoolSelect(e)}
                  renderValue={(selected: string[] | string) =>
                    !(typeof selected === "string")
                      ? selected.map((s) => getTitle(schools, s)).join(", ")
                      : getTitle(schools, selected)
                  }
                  MenuProps={menuStyles}
                >
                  {schools
                    .map((school) => school.school_id)
                    .map((school, index) => {
                      return (
                        <MenuItem key={index} value={school}>
                          <ListItemText primary={getTitle(schools, school!)} />
                        </MenuItem>
                      );
                    })}

                  <MenuItem value={""}>
                    <ListItemText primary={"No Selection"} />
                  </MenuItem>
                </Select>
              </StyledFormControl>
              <StyledFormControl variant='outlined' fullWidth size='small'>
                <InputLabel id='select-school'>Case</InputLabel>
                <Select
                  value={getCaseValue(selectedCase)}
                  label='School'
                  onChange={(e) => handleCaseSelect(e)}
                  MenuProps={menuStyles}
                >
                  <MenuItem value={"all"}>All</MenuItem>
                  <MenuItem value={"case"}>Case Students</MenuItem>
                  <MenuItem value={"not-case"}>Non-Case Students</MenuItem>
                </Select>
              </StyledFormControl>
            </Box>
          </Box>
          <ActionButtons
            role={role}
            permissions={permissions}
            handleClickToUpload={handleClickToUpload}
            handleOpen={handleOpen}
            handleClickToCreate={handleClickToCreate}
          />
        </Toolbar>
      </TableBar>
    );
  });

  if (showLoader && isLoading) return <Loading />;

  return (
    <Container maxWidth='xl'>
      <PermissionControl
        permissionPath={permissions.pages.players.access}
        role={role}
      >
        {data ? (
          <CustomTable
            toolbar={PlayersTableToolbar}
            rows={ProcessRows(data)}
            columns={columns}
            currentPage={currentPage}
            totalRows={totalRows}
            rpp={rpp}
            handlePaginationOptions={handlePaginationOptions}
          />
        ) : (
          <Loading />
        )}
        <Modal
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          open={open}
          onClose={handleClose}
          aria-labelledby='modal-school-form'
          aria-describedby='modal-school-form'
        >
          <div>
            <GenerateStudentsForm handleClose={handleClose} />
          </div>
        </Modal>
      </PermissionControl>
      {(error || alert) && (
        <AlertDialog
          title={error ? "System Error" : "System Warning"}
          message={error || alert || ""}
          buttons={
            error
              ? [
                  {
                    label: "Ok",
                    onClick: () => {
                      setError(undefined);
                    },
                  },
                ]
              : [
                  {
                    label: "Ok",
                    onClick: () => {
                      setAlert(undefined);
                      handleRemoveDelete();
                    },
                  },
                  {
                    label: "Cancel",
                    onClick: () => {
                      setAlert(undefined);
                      setRowDel(undefined);
                      setRowRem(undefined);
                    },
                    outlined: true,
                  },
                ]
          }
        />
      )}
    </Container>
  );
};

export default Players;
